<script lang="ts">
	import ExamSessions from '$lib/components/ExamSessions.svelte';
	import { BookOpen, Clipboard, Award, HelpCircle } from 'lucide-svelte';
</script>

<div class="space-y-8 py-8">
	<div class="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
		<div class="text-center">
			<h1 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-white">
				Amateur Radio License Testing
			</h1>
			<p class="mt-4 text-lg text-gray-600 dark:text-gray-300">
				Get your amateur radio license or upgrade your current one with our VE team
			</p>
		</div>

		<div class="mt-12 space-y-12">
			<!-- Exam Sessions Section -->
			<section>
				<h2 class="text-2xl font-bold dark:text-white">Upcoming Exam Sessions</h2>
				<p class="mt-2 text-gray-600 dark:text-gray-300">
					DSARC offers regular testing sessions for all license classes. Our volunteer examiners are
					certified by the Laurel VEC.
				</p>

				<div class="mt-6">
					<ExamSessions showMoreFilters={false} />
				</div>
			</section>

			<!-- Testing Information -->
			<section>
				<h2 class="text-2xl font-bold dark:text-white">What to Expect</h2>

				<div class="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
					<div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
						<div class="mb-4 flex items-center">
							<Clipboard class="mr-3 h-6 w-6 text-blue-600 dark:text-blue-400" />
							<h3 class="text-lg font-semibold dark:text-white">What to Bring</h3>
						</div>
						<ul class="ml-5 list-disc space-y-2 text-gray-600 dark:text-gray-300">
							<li>Photo ID (driver's license, passport, etc.)</li>
							<li>
								FCC Registration Number (FRN) - <a
									href="https://www.fcc.gov/wireless/support/universal-licensing-system-uls-resources/getting-fcc-registration-number-frn"
									target="_blank"
									rel="noopener noreferrer"
									class="text-blue-600 hover:underline dark:text-blue-400">How to get an FRN</a
								>
							</li>
							<li>Copy of your current license (if upgrading)</li>
							<li>A calculator with the memory cleared (if desired)</li>
							<li>Pencils and scratch paper will be provided</li>
						</ul>
					</div>

					<div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
						<div class="mb-4 flex items-center">
							<Award class="mr-3 h-6 w-6 text-blue-600 dark:text-blue-400" />
							<h3 class="text-lg font-semibold dark:text-white">License Classes</h3>
						</div>
						<ul class="ml-5 list-disc space-y-2 text-gray-600 dark:text-gray-300">
							<li><strong>Technician:</strong> Entry-level license, 35 question exam</li>
							<li><strong>General:</strong> Intermediate license, 35 question exam</li>
							<li><strong>Amateur Extra:</strong> Top-level license, 50 question exam</li>
						</ul>
						<p class="mt-4 text-gray-600 dark:text-gray-300">
							You must pass each license class exam in sequence, but you can take multiple exams in
							one session.
						</p>
					</div>
				</div>
			</section>

			<!-- Study Resources -->
			<section>
				<h2 class="text-2xl font-bold dark:text-white">Study Resources</h2>
				<p class="mt-2 text-gray-600 dark:text-gray-300">
					Prepare for your exam with these helpful resources.
				</p>

				<div class="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
					<a
						href="https://hamstudy.org"
						target="_blank"
						rel="noopener noreferrer"
						class="block rounded-lg bg-white p-6 shadow transition hover:shadow-md dark:bg-gray-800"
					>
						<div class="mb-4 flex items-center">
							<BookOpen class="mr-3 h-6 w-6 text-blue-600 dark:text-blue-400" />
							<h3 class="text-lg font-semibold dark:text-white">HamStudy.org</h3>
						</div>
						<p class="text-gray-600 dark:text-gray-300">
							<strong>Our top recommendation!</strong> Excellent free study tools online, with a paid
							app that's worth every penny. Highly rated by our past test participants.
						</p>
					</a>

					<a
						href="https://hambook.org/"
						target="_blank"
						rel="noopener noreferrer"
						class="block rounded-lg bg-white p-6 shadow transition hover:shadow-md dark:bg-gray-800"
					>
						<div class="mb-4 flex items-center">
							<BookOpen class="mr-3 h-6 w-6 text-blue-600 dark:text-blue-400" />
							<h3 class="text-lg font-semibold dark:text-white">HamBook.org</h3>
						</div>
						<p class="text-gray-600 dark:text-gray-300">
							Comprehensive reference materials and study guides for amateur radio operators of all
							levels.
						</p>
					</a>

					<a
						href="https://www.kb6nu.com/study-guides/"
						target="_blank"
						rel="noopener noreferrer"
						class="block rounded-lg bg-white p-6 shadow transition hover:shadow-md dark:bg-gray-800"
					>
						<div class="mb-4 flex items-center">
							<BookOpen class="mr-3 h-6 w-6 text-blue-600 dark:text-blue-400" />
							<h3 class="text-lg font-semibold dark:text-white">KB6NU Study Guides</h3>
						</div>
						<p class="text-gray-600 dark:text-gray-300">
							Popular "No-Nonsense" study guides for Technician and General licenses.
						</p>
					</a>

					<a
						href="https://www.arrl.org/licensing-education-training"
						target="_blank"
						rel="noopener noreferrer"
						class="block rounded-lg bg-white p-6 shadow transition hover:shadow-md dark:bg-gray-800"
					>
						<div class="mb-4 flex items-center">
							<BookOpen class="mr-3 h-6 w-6 text-blue-600 dark:text-blue-400" />
							<h3 class="text-lg font-semibold dark:text-white">ARRL Resources</h3>
						</div>
						<p class="text-gray-600 dark:text-gray-300">
							Official study guides, practice exams, and training resources from the ARRL.
						</p>
					</a>
				</div>
			</section>

			<!-- FAQ Section -->
			<section>
				<h2 class="text-2xl font-bold dark:text-white">Frequently Asked Questions</h2>

				<div class="mt-6 space-y-4">
					<div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
						<h3 class="text-lg font-semibold dark:text-white">
							How much does it cost to take the exam?
						</h3>
						<p class="mt-2 text-gray-600 dark:text-gray-300">
							Our testing sessions are free of charge. However, upon passing your first exam, you
							will need to pay the FCC a $35 application fee for a new license. There is no FCC fee
							for license upgrades or address changes.
						</p>
					</div>

					<div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
						<h3 class="text-lg font-semibold dark:text-white">
							How long does the testing session take?
						</h3>
						<p class="mt-2 text-gray-600 dark:text-gray-300">
							Plan for about 2 hours total, though the actual time may vary depending on how many
							exams you're taking and how many other candidates are present.
						</p>
					</div>

					<div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
						<h3 class="text-lg font-semibold dark:text-white">When will I receive my license?</h3>
						<p class="mt-2 text-gray-600 dark:text-gray-300">
							After passing your exam, your information will be submitted to the FCC. For new
							licensees, once you pay the FCC application fee, your license should appear in the FCC
							database within a few business days. You can then look up your callsign in the <a
								href="https://wireless2.fcc.gov/UlsApp/UlsSearch/searchLicense.jsp"
								target="_blank"
								rel="noopener noreferrer"
								class="text-blue-600 hover:underline dark:text-blue-400">FCC ULS database</a
							>.
						</p>
					</div>

					<div class="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
						<h3 class="text-lg font-semibold dark:text-white">What if I fail the exam?</h3>
						<p class="mt-2 text-gray-600 dark:text-gray-300">
							If you don't pass, you can retake the exam at another session. We encourage you to
							continue studying and try again. Many people pass on their second attempt after
							focusing on the areas they found challenging.
						</p>
					</div>
				</div>
			</section>

			<!-- Contact for Questions -->
			<section>
				<div class="rounded-lg bg-blue-50 p-6 dark:bg-blue-900/30">
					<div class="flex items-start">
						<div class="flex-shrink-0">
							<HelpCircle class="h-6 w-6 text-blue-600 dark:text-blue-400" />
						</div>
						<div class="ml-3">
							<h3 class="text-lg font-medium text-blue-800 dark:text-blue-300">
								Have more questions?
							</h3>
							<p class="mt-2 text-blue-700 dark:text-blue-200">
								Contact our VE team <a href="/contact" class="font-medium underline">here</a> or join
								us at a club meeting to learn more about amateur radio licensing.
							</p>
						</div>
					</div>
				</div>
			</section>
		</div>
	</div>
</div>
